import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { HistoryEntry, CropUnit } from '../../state/historyStore';
import { useHistoryStore } from '../../state/historyStore';
import { CountryUnitSelector } from './CountryUnitSelector';
import { CropToolbar } from './CropToolbar';
import { PresetGroupsPanel } from './PresetGroupsPanel';
import {
  COUNTRY_OPTIONS,
  CROP_PRESET_GROUPS,
  buildCropMetadata,
  getGroupsForCountry,
  getPresetById,
  type CropPresetGroup,
  type FlattenedPreset
} from './cropPresets';
import {
  clamp,
  convertFromMm,
  formatDimension,
  getAspectRatio,
  normalizeSizeMm,
  TARGET_DPI,
  toPixels,
  type CropSizeMm
} from '../../utils/cropMath';
import { cropImageToSize } from '../../utils/canvasCrop';

interface CropWorkspaceProps {
  entry: HistoryEntry;
  onClose: () => void;
  variant?: 'overlay' | 'embedded';
  toolbarPlacement?: 'inline' | 'portal';
  toolbarPortalTarget?: HTMLElement | null;
}

interface CropSnapshot {
  crop: { x: number; y: number };
  zoom: number;
  unit: CropUnit;
  presetId: string | null;
  customSizeMm: CropSizeMm;
  countryCode: string;
}

const MAX_HISTORY_STEPS = 20;
const DEFAULT_SIZE_MM: CropSizeMm = { width: 25, height: 35 };
const DEFAULT_UNIT: CropUnit = 'mm';

function getDefaultCountryCode(entry: HistoryEntry): string {
  const fromMetadata = entry.result.metadata?.crop?.countryCode;
  if (fromMetadata && COUNTRY_OPTIONS.some((item) => item.code === fromMetadata)) {
    return fromMetadata;
  }
  return COUNTRY_OPTIONS[0]?.code ?? 'CN';
}

function getDefaultPresetId(countryCode: string): string | null {
  const groups = getGroupsForCountry(countryCode);
  return groups[0]?.options[0]?.variations?.[0]?.id ?? null;
}

function getDefaultSize(
  entry: HistoryEntry,
  presetId: string | null,
  fallback: CropSizeMm
): CropSizeMm {
  if (entry.result.metadata?.crop?.sizeMm) {
    return entry.result.metadata.crop.sizeMm;
  }
  const preset = getPresetById(presetId ?? undefined);
  if (preset) {
    return { width: preset.widthMm, height: preset.heightMm };
  }
  return fallback;
}

export function CropWorkspace({
  entry,
  onClose,
  variant = 'overlay',
  toolbarPlacement = 'inline',
  toolbarPortalTarget
}: CropWorkspaceProps) {
  const { t } = useTranslation();
  const updateEntry = useHistoryStore((state) => state.updateEntry);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isOverlay = variant !== 'embedded';
  const shouldRenderInlineToolbar = toolbarPlacement === 'inline';

  const initialCountryCode = getDefaultCountryCode(entry);
  const defaultPresetId = entry.result.metadata?.crop?.presetId ?? getDefaultPresetId(initialCountryCode);
  const initialSize = getDefaultSize(entry, defaultPresetId, DEFAULT_SIZE_MM);
  const initialUnit = entry.result.metadata?.crop?.unit ?? DEFAULT_UNIT;

  const [countryCode, setCountryCode] = useState(initialCountryCode);
  const [unit, setUnit] = useState<CropUnit>(initialUnit);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(defaultPresetId);
  const [customSizeMm, setCustomSizeMm] = useState<CropSizeMm>(initialSize);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [cropAreaSize, setCropAreaSize] = useState<{ width: number; height: number } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(!isOverlay);
  const [undoStack, setUndoStack] = useState<CropSnapshot[]>([
    {
      crop: { x: 0, y: 0 },
      zoom: 1,
      unit: initialUnit,
      presetId: defaultPresetId ?? null,
      customSizeMm: initialSize,
      countryCode: initialCountryCode
    }
  ]);
  const [redoStack, setRedoStack] = useState<CropSnapshot[]>([]);
  const [viewportNode, setViewportNode] = useState<HTMLDivElement | null>(null);
  const [viewportSize, setViewportSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0
  });
  const handleViewportRef = useCallback((node: HTMLDivElement | null) => {
    setViewportNode(node);
  }, []);

  useEffect(() => {
    if (!isOverlay) {
      return;
    }
    setIsMounted(true);
    if (typeof document === 'undefined') {
      return;
    }
    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOverlay]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined' || !viewportNode) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setViewportSize({ width, height });
    });
    observer.observe(viewportNode);
    return () => {
      observer.disconnect();
    };
  }, [viewportNode]);

  const availableGroups = useMemo<CropPresetGroup[]>(() => {
    const groups = getGroupsForCountry(countryCode);
    return groups.length ? groups : CROP_PRESET_GROUPS;
  }, [countryCode]);

  const activePreset = useMemo<FlattenedPreset | undefined>(() => {
    return getPresetById(selectedPresetId ?? undefined);
  }, [selectedPresetId]);

  const currentSizeMm = useMemo(() => {
    if (activePreset) {
      return { width: activePreset.widthMm, height: activePreset.heightMm };
    }
    return normalizeSizeMm(customSizeMm);
  }, [activePreset, customSizeMm]);

  const aspectRatio = useMemo(() => getAspectRatio(currentSizeMm), [currentSizeMm]);
  const imageAspectRatio = useMemo(() => {
    const width = Math.max(1, entry.result.width || 1);
    const height = Math.max(1, entry.result.height || 1);
    return width / height;
  }, [entry.result.height, entry.result.width]);

  const cropViewportMetrics = useMemo(() => {
    const { width, height } = viewportSize;
    if (!width || !height) {
      return {
        style: { width: '100%', height: '100%' },
        width: width ?? 0,
        height: height ?? 0
      };
    }
    const containerAspect = width / height;
    if (containerAspect > imageAspectRatio) {
      const adjustedHeight = height;
      const adjustedWidth = adjustedHeight * imageAspectRatio;
      return {
        style: { width: `${adjustedWidth}px`, height: `${adjustedHeight}px` },
        width: adjustedWidth,
        height: adjustedHeight
      };
    }
    const adjustedWidth = width;
    const adjustedHeight = adjustedWidth / imageAspectRatio;
    return {
      style: { width: `${adjustedWidth}px`, height: `${adjustedHeight}px` },
      width: adjustedWidth,
      height: adjustedHeight
    };
  }, [imageAspectRatio, viewportSize]);

  const cropViewportStyle = cropViewportMetrics.style;
  const renderedWidth = cropViewportMetrics.width;
  const renderedHeight = cropViewportMetrics.height;

  const cropAreaOffsets = useMemo(() => {
    if (!cropAreaSize || renderedWidth <= 0 || renderedHeight <= 0) {
      return null;
    }
    const offsetX = Math.max(0, (renderedWidth - cropAreaSize.width) / 2);
    const offsetY = Math.max(0, (renderedHeight - cropAreaSize.height) / 2);
    return {
      x: offsetX,
      y: offsetY,
      width: cropAreaSize.width,
      height: cropAreaSize.height
    };
  }, [cropAreaSize, renderedHeight, renderedWidth]);

  useEffect(() => {
    if (selectedPresetId) {
      const preset = getPresetById(selectedPresetId);
      if (preset && preset.countries && !preset.countries.includes(countryCode)) {
        const fallback = getDefaultPresetId(countryCode);
        setSelectedPresetId(fallback);
        if (fallback) {
          const fallbackPreset = getPresetById(fallback);
          if (fallbackPreset) {
            setCustomSizeMm({ width: fallbackPreset.widthMm, height: fallbackPreset.heightMm });
          }
        }
      }
    }
  }, [countryCode, selectedPresetId]);

  const recordSnapshot = useCallback(
    (overrides?: Partial<CropSnapshot>) => {
      setUndoStack((previous) => {
        const snapshot: CropSnapshot = {
          crop: overrides?.crop ?? { ...crop },
          zoom: overrides?.zoom ?? zoom,
          unit: overrides?.unit ?? unit,
          presetId: overrides?.presetId ?? selectedPresetId,
          customSizeMm: overrides?.customSizeMm ?? customSizeMm,
          countryCode: overrides?.countryCode ?? countryCode
        };
        const nextStack = [...previous, snapshot];
        if (nextStack.length > MAX_HISTORY_STEPS) {
          nextStack.shift();
        }
        return nextStack;
      });
      setRedoStack([]);
    },
    [countryCode, crop, customSizeMm, selectedPresetId, unit, zoom]
  );

  const canUndo = undoStack.length > 1;
  const canRedo = redoStack.length > 0;

  const handleUndo = useCallback(() => {
    setUndoStack((previous) => {
      if (previous.length <= 1) {
        return previous;
      }
      const current = previous[previous.length - 1];
      const nextStack = previous.slice(0, previous.length - 1);
      const snapshot = nextStack[nextStack.length - 1];
      setRedoStack((redo) => [...redo, current]);
      setCrop(snapshot.crop);
      setZoom(snapshot.zoom);
      setUnit(snapshot.unit);
      setSelectedPresetId(snapshot.presetId);
      setCustomSizeMm(snapshot.customSizeMm);
      setCountryCode(snapshot.countryCode);
      return nextStack;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setRedoStack((previous) => {
      if (!previous.length) {
        return previous;
      }
      const snapshot = previous[previous.length - 1];
      setUndoStack((undo) => [...undo, snapshot]);
      setCrop(snapshot.crop);
      setZoom(snapshot.zoom);
      setUnit(snapshot.unit);
      setSelectedPresetId(snapshot.presetId);
      setCustomSizeMm(snapshot.customSizeMm);
      setCountryCode(snapshot.countryCode);
      return previous.slice(0, previous.length - 1);
    });
  }, []);

  const handleCountryChange = useCallback(
    (code: string) => {
      setCountryCode(code);
      const fallback = getDefaultPresetId(code);
      if (fallback) {
        const preset = getPresetById(fallback);
        if (preset) {
          const nextSize = { width: preset.widthMm, height: preset.heightMm };
          setSelectedPresetId(fallback);
          setCustomSizeMm(nextSize);
          recordSnapshot({
            presetId: fallback,
            customSizeMm: nextSize,
            countryCode: code
          });
          return;
        }
      }
      recordSnapshot({
        countryCode: code
      });
    },
    [recordSnapshot]
  );

  const handleUnitChange = useCallback(
    (nextUnit: CropUnit) => {
      setUnit(nextUnit);
      recordSnapshot({ unit: nextUnit });
    },
    [recordSnapshot]
  );

  const handlePresetSelect = useCallback(
    (presetId: string, preset: FlattenedPreset) => {
      setSelectedPresetId(presetId);
      const nextSize = { width: preset.widthMm, height: preset.heightMm };
      setCustomSizeMm(nextSize);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      recordSnapshot({
        presetId,
        customSizeMm: nextSize,
        crop: { x: 0, y: 0 },
        zoom: 1
      });
    },
    [recordSnapshot]
  );
  const handleInteractionEnd = useCallback(() => {
    recordSnapshot();
  }, [recordSnapshot]);

  const sizeLabel = useMemo(() => {
    return `${formatDimension(currentSizeMm.width, unit)} × ${formatDimension(currentSizeMm.height, unit)} ${t(
      `history.crop.units.${unit}`
    )}`;
  }, [currentSizeMm.height, currentSizeMm.width, t, unit]);

  const targetWidthPx = toPixels(currentSizeMm.width, TARGET_DPI);
  const targetHeightPx = toPixels(currentSizeMm.height, TARGET_DPI);

  const exportCurrentCrop = useCallback(async () => {
    if (!croppedArea) {
      throw new Error(t('history.crop.errors.noArea'));
    }
    return cropImageToSize({
      imageSrc: entry.result.dataUrl,
      cropArea: croppedArea,
      targetWidth: targetWidthPx,
      targetHeight: targetHeightPx,
      mimeType: 'image/webp'
    });
  }, [croppedArea, entry.result.dataUrl, targetHeightPx, targetWidthPx, t]);

  const handleDownload = useCallback(async () => {
    try {
      const result = await exportCurrentCrop();
      triggerDownload({
        dataUrl: result.dataUrl,
        fileName: buildFileName(entry, activePreset, countryCode)
      });
    } catch (error) {
      console.warn('Failed to download cropped image', error);
      setErrorMessage(t('history.crop.errors.exportFailed'));
    }
  }, [activePreset, countryCode, entry, exportCurrentCrop, t]);

  const handleSave = useCallback(async () => {
    setErrorMessage(null);
    setIsSaving(true);
    try {
      const result = await exportCurrentCrop();
      await updateEntry(entry.id, (source) => {
        const nextStorageBytes =
          estimateDataUrlBytes(source.original.dataUrl) + estimateDataUrlBytes(result.dataUrl);
        const preset = getPresetById(selectedPresetId ?? undefined);
        return {
          ...source,
          result: {
            ...source.result,
            dataUrl: result.dataUrl,
            width: result.width,
            height: result.height,
            originalDataUrl: source.result.originalDataUrl ?? source.result.dataUrl,
            originalWidth: source.result.originalWidth ?? source.result.width,
            originalHeight: source.result.originalHeight ?? source.result.height,
            metadata: {
              ...source.result.metadata,
              crop: buildCropMetadata({
                countryCode,
                unit,
                preset,
                sizeMm: currentSizeMm
              })
            }
          },
          storageBytes: nextStorageBytes
        };
      });
      onClose();
    } catch (error) {
      console.error('Failed to save crop result', error);
      setErrorMessage(t('history.crop.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  }, [countryCode, currentSizeMm, entry.id, exportCurrentCrop, onClose, selectedPresetId, t, unit, updateEntry]);

  const handleRestoreOriginal = useCallback(async () => {
    const originalDataUrl = entry.result.originalDataUrl ?? entry.result.dataUrl;
    const originalWidth = entry.result.originalWidth ?? entry.result.width;
    const originalHeight = entry.result.originalHeight ?? entry.result.height;
    try {
      await updateEntry(entry.id, (source) => ({
        ...source,
        result: {
          ...source.result,
          dataUrl: originalDataUrl,
          width: originalWidth,
          height: originalHeight,
          metadata: {
            ...source.result.metadata,
            crop: undefined
          }
        },
        storageBytes: estimateDataUrlBytes(source.original.dataUrl) + estimateDataUrlBytes(originalDataUrl)
      }));
      setSelectedPresetId(null);
      setCustomSizeMm(DEFAULT_SIZE_MM);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setErrorMessage(null);
      onClose();
    } catch (error) {
      console.error('Failed to restore original image', error);
      setErrorMessage(t('history.crop.errors.restoreFailed'));
    }
  }, [
    entry.id,
    entry.result.dataUrl,
    entry.result.originalDataUrl,
    entry.result.originalHeight,
    entry.result.originalWidth,
    onClose,
    t,
    updateEntry
  ]);

  const renderToolbar = useCallback(
    () => (
      <CropToolbar
        t={t}
        canRedo={canRedo}
        canUndo={canUndo}
        canRestoreOriginal={Boolean(entry.result.originalDataUrl)}
        isSaving={isSaving}
        onCancel={onClose}
        onDownload={handleDownload}
        onRedo={handleRedo}
        onRestoreOriginal={handleRestoreOriginal}
        onSave={handleSave}
        onUndo={handleUndo}
      />
    ),
    [
      canRedo,
      canUndo,
      entry.result.originalDataUrl,
      handleDownload,
      handleRedo,
      handleRestoreOriginal,
      handleSave,
      handleUndo,
      isSaving,
      onClose,
      t
    ]
  );

  const renderToolbarPortal = () =>
    toolbarPortalTarget ? createPortal(renderToolbar(), toolbarPortalTarget) : null;

  const content = (
    <div
      ref={containerRef}
      className={clsx(
        'flex h-full w-full flex-col gap-4 rounded-3xl border border-ai-surface-glow/30 bg-ai-surface p-4 shadow-ai-card',
        isOverlay ? 'max-w-6xl' : 'h-full rounded-none border-none bg-transparent p-0 shadow-none'
      )}
    >
      <header className={clsx('flex flex-wrap items-center justify-between gap-3', isOverlay ? '' : 'pb-2')}>
        {isOverlay ? (
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-ai-text-secondary">
              {entry.style.name}
            </p>
            <h3 className="font-display text-2xl font-semibold text-ai-text-primary">
              {t('history.crop.heading')}
            </h3>
          </div>
        ) : null}
        {shouldRenderInlineToolbar ? renderToolbar() : null}
      </header>
      {toolbarPlacement === 'portal' && toolbarPortalTarget ? renderToolbarPortal() : null}
      <div className="flex flex-1 gap-4 overflow-hidden">
        <aside className="w-full max-w-sm space-y-3 overflow-y-auto rounded-2xl border border-ai-surface-glow/20 bg-ai-surface-elevated/60 p-4 text-sm">
          <CountryUnitSelector
            countryCode={countryCode}
            unit={unit}
            onCountryChange={handleCountryChange}
            onUnitChange={handleUnitChange}
            t={t}
          />
          <PresetGroupsPanel
            groups={availableGroups}
            selectedPresetId={selectedPresetId}
            unit={unit}
            onSelectPreset={handlePresetSelect}
            t={t}
          />
        </aside>
        <div className="flex flex-1 flex-col gap-3">
          <div className="relative flex-1 overflow-hidden rounded-3xl border border-ai-surface-glow/30 bg-black/20">
            <div ref={handleViewportRef} className="flex h-full w-full items-center justify-center">
              <div className="relative" style={cropViewportStyle}>
                <Cropper
                  image={entry.result.dataUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspectRatio}
                  onCropChange={setCrop}
                  onZoomChange={(value) => setZoom(clamp(value, 1, 8))}
                  onCropComplete={(_, area) => setCroppedArea(area)}
                  onInteractionEnd={handleInteractionEnd}
                  objectFit="contain"
                  showGrid={false}
                  onCropSizeChange={(size) => setCropAreaSize(size)}
                />
                {cropAreaOffsets ? (
                  <>
                    <Ruler
                      orientation="horizontal"
                      unit={unit}
                      sizeMm={currentSizeMm.width}
                      offsetX={cropAreaOffsets.x}
                      offsetY={cropAreaOffsets.y}
                      lengthPx={cropAreaOffsets.width}
                    />
                    <Ruler
                      orientation="vertical"
                      unit={unit}
                      sizeMm={currentSizeMm.height}
                      offsetX={cropAreaOffsets.x}
                      offsetY={cropAreaOffsets.y}
                      lengthPx={cropAreaOffsets.height}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/60 p-4 text-sm text-ai-text-secondary">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.3em]">{t('history.crop.sizeTitle')}</p>
                <p className="text-lg font-semibold text-ai-text-primary">{sizeLabel}</p>
                <p>
                  {t('history.crop.pixelHint', {
                    width: targetWidthPx,
                    height: targetHeightPx,
                    dpi: TARGET_DPI
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span>{t('history.crop.zoom')}</span>
                <input
                  type="range"
                  min={1}
                  max={8}
                  step={0.1}
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                />
              </div>
            </div>
          </div>
          {errorMessage ? (
            <div
              role="alert"
              className="rounded-2xl border border-ai-warning/40 bg-ai-warning/10 p-3 text-sm text-ai-warning"
            >
              {errorMessage}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (!isOverlay) {
    return content;
  }

  if (!isMounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4 py-6 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-label={t('history.crop.heading')}
    >
      {content}
    </div>,
    document.body
  );
}

interface RulerProps {
  orientation: 'horizontal' | 'vertical';
  unit: CropUnit;
  sizeMm: number;
  offsetX: number;
  offsetY: number;
  lengthPx: number;
}

function Ruler({ orientation, unit, sizeMm, offsetX, offsetY, lengthPx }: RulerProps) {
  const clampedLength = Math.max(1, lengthPx);
  const clampedOffsetX = Math.max(0, offsetX);
  const clampedOffsetY = Math.max(0, offsetY);
  const positionStyle =
    orientation === 'horizontal'
      ? { left: `${clampedOffsetX}px`, top: `${clampedOffsetY}px`, width: `${clampedLength}px` }
      : { left: `${clampedOffsetX}px`, top: `${clampedOffsetY}px`, height: `${clampedLength}px` };
  const ticks = useMemo(() => {
    const converted = Math.max(convertFromMm(sizeMm, unit), 0.001);
    const integerPart = Math.floor(converted);
    const values: number[] = [];

    for (let value = 0; value <= integerPart; value += 1) {
      values.push(value);
    }
    if (Math.abs(values[values.length - 1] - converted) > 0.001) {
      values.push(Number(converted.toFixed(2)));
    }

    return values.map((value) => {
      const rounded = Math.round(value);
      const isInteger = Math.abs(value - rounded) < 0.001;
      const isMajor = value === 0 || (isInteger && rounded % 5 === 0);
      const shouldLabel = isMajor && isInteger;
      return {
        key: `${value}`,
        position: (value / converted) * 100,
        isMajor,
        label: shouldLabel ? rounded : null
      };
    });
  }, [sizeMm, unit]);

  return (
    <div
      className={clsx(
        'pointer-events-none absolute text-[10px] text-ai-text-secondary/80',
        orientation === 'horizontal'
          ? 'h-10'
          : 'w-10'
      )}
      style={positionStyle}
    >
      <div className="relative h-full w-full">
        {ticks.map((tick) => (
          <span
            key={`${orientation}-${tick.key}`}
            className={clsx(
              'absolute text-[10px] text-ai-text-secondary/80',
              orientation === 'horizontal'
                ? 'flex -translate-x-1/2 flex-col items-center justify-start'
                : 'flex -translate-y-1/2 flex-row items-center'
            )}
            style={
              orientation === 'horizontal'
                ? { left: `${tick.position}%`, top: 0 }
                : { left: 0, top: `${tick.position}%` }
            }
          >
            <span
              className={clsx(
                'bg-white/60',
                orientation === 'horizontal'
                  ? tick.isMajor
                    ? 'h-4 w-px'
                    : 'h-2 w-px'
                  : tick.isMajor
                    ? 'w-4 h-px'
                    : 'w-2 h-px'
              )}
            />
            {tick.label !== null ? (
              <span
                className={clsx(
                  'rounded-full bg-black/60 px-1 py-0.5',
                  orientation === 'horizontal' ? 'mt-1' : 'ml-1'
                )}
              >
                {tick.label}
              </span>
            ) : null}
          </span>
        ))}
      </div>
    </div>
  );
}

function estimateDataUrlBytes(dataUrl: string): number {
  const commaIndex = dataUrl.indexOf(',');
  const base64 = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  const paddingMatch = base64.match(/=+$/);
  const padding = paddingMatch ? paddingMatch[0].length : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function triggerDownload(params: { dataUrl: string; fileName: string }) {
  const link = document.createElement('a');
  link.href = params.dataUrl;
  link.download = params.fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function buildFileName(entry: HistoryEntry, preset: FlattenedPreset | undefined, countryCode: string): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const safeStyle = entry.style.name.replace(/\s+/g, '-');
  const presetLabel = preset ? preset.id : 'custom';
  return `${safeStyle}-${presetLabel}-${countryCode}-${timestamp}.webp`;
}
